/**
 * Weight Analysis Utilities
 * 
 * Provides consistent weight analysis across the application
 * using only legitimate weight data sources.
 */

/**
 * Analyzes weight data from legitimate sources
 * @param {Array} healthData - Array of health report data 
 * @param {Object} clientStatistics - Client statistics object
 * @returns {Object} Weight analysis results
 */
export const analyzeWeight = (healthData = [], clientStatistics = {}) => {
  // Get weights from health data (daily reports)
  const healthWeights = healthData
    .map((data) => data.reportData?.weight)
    .filter((weight) => weight && weight > 0);

  // Get the latest weight from health data or client statistics
  const latestWeight = healthWeights.length > 0 
    ? healthWeights[healthWeights.length - 1] 
    : clientStatistics.currentWeight ?? clientStatistics.weight;

  // Calculate average weight from health data
  const averageWeight = healthWeights.length > 0 
    ? healthWeights.reduce((sum, w) => sum + w, 0) / healthWeights.length 
    : null;

  // Get starting weight
  const startingWeight = clientStatistics.startingWeight;

  // Calculate weight trend if we have multiple data points
  let trend = null;
  if (healthWeights.length >= 2) {
    const firstWeight = healthWeights[0];
    const lastWeight = healthWeights[healthWeights.length - 1];
    const change = lastWeight - firstWeight;
    trend = {
      change: change,
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
      percentage: firstWeight > 0 ? (change / firstWeight) * 100 : 0
    };
  }

  return {
    latestWeight: latestWeight > 0 ? latestWeight : null,
    averageWeight,
    startingWeight: startingWeight > 0 ? startingWeight : null,
    totalDataPoints: healthWeights.length,
    trend,
    hasData: (latestWeight > 0) || (averageWeight > 0) || (startingWeight > 0)
  };
};

/**
 * Gets the best available weight for macro calculations
 * Follows priority: currentWeight > startingWeight > weight
 * @param {Object} clientStatistics - Client statistics object
 * @returns {number|null} Best available weight
 */
export const getBestWeight = (clientStatistics = {}) => {
  if (clientStatistics.currentWeight && clientStatistics.currentWeight > 0) {
    return clientStatistics.currentWeight;
  }

  if (clientStatistics.startingWeight && clientStatistics.startingWeight > 0) {
    return clientStatistics.startingWeight;
  }

  if (clientStatistics.weight && clientStatistics.weight > 0) {
    return clientStatistics.weight;
  }

  return null;
};

/**
 * Calculates weight change between two values
 * @param {number} startingWeight - Starting weight
 * @param {number} currentWeight - Current weight
 * @returns {Object} Weight change analysis
 */
export const calculateWeightChange = (startingWeight, currentWeight) => {
  if (!startingWeight || !currentWeight || startingWeight <= 0 || currentWeight <= 0) {
    return null;
  }

  const change = currentWeight - startingWeight;
  const percentChange = (change / startingWeight) * 100;

  return {
    change,
    percentChange,
    direction: change > 0 ? 'gained' : change < 0 ? 'lost' : 'maintained',
    isSignificant: Math.abs(change) >= 1 // 1 lb or more is considered significant
  };
};

/**
 * Formats weight display consistently
 * @param {number} weight - Weight value
 * @param {string} unit - Unit (default: 'lbs')
 * @returns {string} Formatted weight string
 */
export const formatWeight = (weight, unit = 'lbs') => {
  if (!weight || weight <= 0) {
    return 'Not Set';
  }
  return `${Math.round(weight)} ${unit}`;
};
